import { dateRangesEqual, isDateRangeSet } from "./dateUtils";
import { filterDisplayColumn } from "./rangeGroupingUtils";
import {
  FILTER_KIND_LABEL_SEPARATOR,
  INPUT_COLUMN_FILTER_CHANGE_TYPE,
  INPUT_DATE_FILTER_CHANGE_TYPE,
  INPUT_NUMBER_FILTER_CHANGE_TYPE,
} from "components/reportsV3/types/constants";
import * as labels from "config.labels";
import { isEqual } from "lodash";

const changeTypeLabels = (t) => ({
  columns: t(labels.CHANGE_DETECTOR_COLUMNS, { default: "Columns" }),
  sortOrder: t(labels.CHANGE_DETECTOR_SORT_ORDER, { default: "Sort Order" }),
});

export const arraysEqual = (a, b) =>
  a.length === b.length && a.every((val, i) => b[i] === val);

const itemsAtEnd = (items, array) =>
  items.length === 0 ||
  items.every((item, i) => array[array.length - items.length + i] === item);

const itemsAtStart = (items, array) =>
  items.length === 0 || items.every((item, i) => array[i] === item);

function optionWord(size, t) {
  return size === 1
    ? t(labels.CHANGE_DETECTOR_OPTION, { default: "option" })
    : t(labels.CHANGE_DETECTOR_OPTIONS, { default: "options" });
}

function detectFilterChanges(
  currentMap,
  savedMap,
  config,
  columnDisplayNameMap,
  now,
  t
) {
  const result = [];
  const allKeys = new Set([
    ...Object.keys(currentMap),
    ...Object.keys(savedMap),
  ]);

  allKeys.forEach((column) => {
    const displayColumn = filterDisplayColumn(column);
    const current = currentMap[column];
    const saved = savedMap[column];
    const hasCurrent = config.hasFilter(current);
    const hasSaved = config.hasFilter(saved);

    if (!hasCurrent && !hasSaved) return;

    const hasChange =
      hasCurrent !== hasSaved ||
      (current !== undefined &&
        saved !== undefined &&
        !config.isEqual(current, saved));
    if (!hasChange) return;

    const filterAction =
      !hasSaved && hasCurrent
        ? "added"
        : hasSaved && !hasCurrent
        ? "removed"
        : "modified";

    const description = config.describeChange(
      filterAction,
      column,
      current,
      saved,
      t
    );
    const filterGroups =
      hasCurrent && config.buildFilterGroups
        ? config.buildFilterGroups(
            column,
            columnDisplayNameMap[displayColumn] ?? displayColumn,
            current,
            t
          )
        : [];

    result.push({
      type: config.changeType,
      label: displayColumn,
      description,
      status: "pending",
      state: {},
      timestamp: now,
      column,
      displayColumn,
      filterAction,
      ...(filterGroups.length > 0 && { filterGroups }),
    });
  });

  return result;
}

const valueFilterConfig = {
  changeType: "columnFilter",
  hasFilter: (f) => (f?.size ?? 0) > 0,
  isEqual: (a, b) => a.size === b.size && [...a].every((v) => b.has(v)),
  describeChange: (action, _column, current, saved, t) => {
    const size = current?.size ?? 0;
    const savedSize = saved?.size ?? 0;
    if (action === "removed") {
      return `${t(labels.CHANGE_DETECTOR_REMOVED_VALUE_FILTER, {
        default: "Removed filter (was",
      })} ${savedSize} ${optionWord(savedSize, t)})`;
    }
    const optionLabel = `${size} ${optionWord(size, t)}`;
    if (action === "added") {
      return `${t(labels.CHANGE_DETECTOR_ADDED_VALUE_FILTER, {
        default: "Added filter:",
      })} ${optionLabel}`;
    }
    return `${t(labels.CHANGE_DETECTOR_CHANGED_VALUE_FILTER, {
      default: "Changed:",
    })} ${optionLabel}`;
  },
  buildFilterGroups: (column, label, current, t) => {
    const size = current.size;
    const optionLabel = `${size} ${optionWord(size, t)}`;
    return [
      {
        column,
        columnLabel: label,
        chips: [{ key: `${column}-values`, label: optionLabel }],
      },
    ];
  },
};

const dateFilterConfig = {
  changeType: "dateFilter",
  hasFilter: (f) => isDateRangeSet(f),
  isEqual: dateRangesEqual,
  describeChange: (action, _column, _current, _saved, t) =>
    action === "removed"
      ? t(labels.CHANGE_DETECTOR_REMOVED_DATE_FILTER, {
          default: "Removed date filter",
        })
      : action === "added"
      ? t(labels.CHANGE_DETECTOR_ADDED_DATE_FILTER, {
          default: "Added date filter",
        })
      : t(labels.CHANGE_DETECTOR_CHANGED_DATE_FILTER, {
          default: "Changed date filter",
        }),
};

const numberFilterConfig = {
  changeType: "numberFilter",
  hasFilter: (f) => f !== undefined && (f.min !== null || f.max !== null),
  isEqual: (a, b) => a.min === b.min && a.max === b.max,
  describeChange: (action, _column, _current, _saved, t) =>
    action === "removed"
      ? t(labels.CHANGE_DETECTOR_REMOVED_NUMBER_FILTER, {
          default: "Removed number filter",
        })
      : action === "added"
      ? t(labels.CHANGE_DETECTOR_ADDED_NUMBER_FILTER, {
          default: "Added number filter",
        })
      : t(labels.CHANGE_DETECTOR_CHANGED_NUMBER_FILTER, {
          default: "Changed number filter",
        }),
};

/** Returns an input-filter config derived from an output-filter config. */
const toInputFilterConfig = (config, changeType) => ({
  ...config,
  changeType,
  describeChange: (action, column, current, saved, t) =>
    `${t(labels.REPORTS_V3_FILTER_KIND_ROWS, {
      default: "Filter rows (before grouping)",
    })}${FILTER_KIND_LABEL_SEPARATOR}${config.describeChange(
      action,
      column,
      current,
      saved,
      t
    )}`,
});

const inputValueFilterConfig = toInputFilterConfig(
  valueFilterConfig,
  INPUT_COLUMN_FILTER_CHANGE_TYPE
);
const inputDateFilterConfig = toInputFilterConfig(
  dateFilterConfig,
  INPUT_DATE_FILTER_CHANGE_TYPE
);
const inputNumberFilterConfig = toInputFilterConfig(
  numberFilterConfig,
  INPUT_NUMBER_FILTER_CHANGE_TYPE
);

const EMPTY_FILTER_MAPS = { value: {}, date: {}, number: {} };

export function detectColumnChanges(params) {
  const {
    columnOrder,
    savedColumnOrder,
    pinnedColumns,
    savedPinnedColumns,
    columnVisibilityModel,
    savedColumnVisibility,
    now,
    t,
  } = params;

  const orderChanged = !arraysEqual(columnOrder, savedColumnOrder);

  const allVisibilityKeys = [
    ...new Set([
      ...Object.keys(columnVisibilityModel),
      ...Object.keys(savedColumnVisibility),
    ]),
  ];
  const hasVisibilityChanges = allVisibilityKeys.some((key) => {
    return (
      (columnVisibilityModel[key] ?? true) !==
      (savedColumnVisibility[key] ?? true)
    );
  });

  const pinnedChanged = !arraysEqual(pinnedColumns, savedPinnedColumns);

  const newlyPinnedList = pinnedColumns.filter(
    (col) => !savedPinnedColumns.includes(col)
  );
  const newlyUnpinnedList = savedPinnedColumns.filter(
    (col) => !pinnedColumns.includes(col)
  );

  let orderChangeExplainedByPinning = false;
  if (orderChanged && pinnedChanged) {
    const newlyPinnedSet = new Set(newlyPinnedList);
    const newlyUnpinnedSet = new Set(newlyUnpinnedList);
    const pinnedSet = new Set(pinnedColumns);
    const savedPinnedSet = new Set(savedPinnedColumns);

    const currentUnpinned = columnOrder.filter((col) => !pinnedSet.has(col));
    const savedUnpinned = savedColumnOrder.filter(
      (col) => !savedPinnedSet.has(col)
    );

    const savedUnpinnedFiltered = savedUnpinned.filter(
      (col) => !newlyPinnedSet.has(col)
    );
    const currentUnpinnedFiltered = currentUnpinned.filter(
      (col) => !newlyUnpinnedSet.has(col)
    );
    const savedPinnedFiltered = savedPinnedColumns.filter(
      (col) => !newlyUnpinnedSet.has(col)
    );
    const currentPinnedFiltered = pinnedColumns.filter(
      (col) => !newlyPinnedSet.has(col)
    );

    orderChangeExplainedByPinning =
      itemsAtEnd(newlyPinnedList, pinnedColumns) &&
      itemsAtStart(newlyUnpinnedList, currentUnpinned) &&
      arraysEqual(savedUnpinnedFiltered, currentUnpinnedFiltered) &&
      arraysEqual(savedPinnedFiltered, currentPinnedFiltered);
  }

  const hasActualOrderChange = orderChanged && !orderChangeExplainedByPinning;

  let pinningDescription = "";
  if (pinnedChanged) {
    if (newlyPinnedList.length > 0 && newlyUnpinnedList.length === 0) {
      pinningDescription =
        newlyPinnedList.length === 1
          ? t(labels.CHANGE_DETECTOR_COLUMN_PINNED, {
              default: "Column pinned",
            })
          : t(labels.CHANGE_DETECTOR_COLUMNS_PINNED, {
              default: "Columns pinned",
            });
    } else if (newlyUnpinnedList.length > 0 && newlyPinnedList.length === 0) {
      pinningDescription =
        newlyUnpinnedList.length === 1
          ? t(labels.CHANGE_DETECTOR_COLUMN_UNPINNED, {
              default: "Column unpinned",
            })
          : t(labels.CHANGE_DETECTOR_COLUMNS_UNPINNED, {
              default: "Columns unpinned",
            });
    } else {
      pinningDescription = t(labels.CHANGE_DETECTOR_PINNING_CHANGED, {
        default: "Pinning changed",
      });
    }
  }

  if (
    !(
      (hasActualOrderChange && columnOrder.length > 0) ||
      hasVisibilityChanges ||
      pinnedChanged
    )
  ) {
    return null;
  }

  const descriptions = [];
  if (hasActualOrderChange)
    descriptions.push(
      t(labels.CHANGE_DETECTOR_ORDER_CHANGED, { default: "order changed" })
    );
  if (hasVisibilityChanges)
    descriptions.push(
      t(labels.CHANGE_DETECTOR_VISIBILITY_CHANGED, {
        default: "visibility changed",
      })
    );

  const columnOrderInfo = {
    pinnedColumns: [...pinnedColumns],
    savedPinnedColumns: [...savedPinnedColumns],
  };

  const typeLabels = changeTypeLabels(t);
  const andWord = t(labels.CHANGE_DETECTOR_AND, { default: "and" });
  const columnPrefix = t(labels.CHANGE_DETECTOR_COLUMN_PREFIX, {
    default: "Column",
  });

  let description;
  if (descriptions.length === 0 && pinnedChanged) {
    description = pinningDescription;
  } else if (pinnedChanged) {
    descriptions.push(pinningDescription.toLowerCase());
    description = `${columnPrefix} ${descriptions.join(` ${andWord} `)}`;
  } else {
    description = `${columnPrefix} ${descriptions.join(` ${andWord} `)}`;
  }

  return {
    type: "columns",
    label: typeLabels.columns,
    description,
    status: "pending",
    state: {},
    timestamp: now,
    columnOrderInfo,
  };
}

export function detectSortChanges({ sortModel, savedSortModel, now, t }) {
  const sortChanged =
    sortModel.length !== savedSortModel.length ||
    sortModel.some((sort, idx) => {
      const saved = savedSortModel[idx];
      return !saved || sort.field !== saved.field || sort.sort !== saved.sort;
    });

  if (!sortChanged) return null;

  const typeLabels = changeTypeLabels(t);
  const description =
    sortModel.length > 0
      ? `${t(labels.CHANGE_DETECTOR_SORTED_BY, {
          default: "Sorted by",
        })} ${sortModel.map((s) => `${s.field} (${s.sort})`).join(", ")}`
      : t(labels.CHANGE_DETECTOR_SORT_CLEARED, {
          default: "Sort order cleared",
        });

  return {
    type: "sortOrder",
    label: typeLabels.sortOrder,
    description,
    status: "pending",
    state: {},
    timestamp: now,
  };
}

export function detectAllFilterChanges({
  filterMaps,
  savedFilterMaps,
  inputFilterMaps = EMPTY_FILTER_MAPS,
  savedInputFilterMaps = EMPTY_FILTER_MAPS,
  columnDisplayNameMap,
  now,
  t,
}) {
  const passes = [
    [filterMaps, savedFilterMaps, "value", valueFilterConfig],
    [filterMaps, savedFilterMaps, "date", dateFilterConfig],
    [filterMaps, savedFilterMaps, "number", numberFilterConfig],
    [inputFilterMaps, savedInputFilterMaps, "value", inputValueFilterConfig],
    [inputFilterMaps, savedInputFilterMaps, "date", inputDateFilterConfig],
    [inputFilterMaps, savedInputFilterMaps, "number", inputNumberFilterConfig],
  ];
  return passes.flatMap(([current, saved, key, config]) =>
    detectFilterChanges(
      current[key],
      saved[key],
      config,
      columnDisplayNameMap,
      now,
      t
    )
  );
}

export function detectColumnWidthChanges({
  columnWidths,
  savedColumnWidths,
  now,
  t,
}) {
  const allKeys = new Set([
    ...Object.keys(columnWidths),
    ...Object.keys(savedColumnWidths),
  ]);

  const hasChanges = [...allKeys].some(
    (key) => columnWidths[key] !== savedColumnWidths[key]
  );
  if (!hasChanges) return null;

  return {
    type: "columnWidths",
    label: t(labels.CHANGE_DETECTOR_COLUMN_WIDTHS, {
      default: "Column Widths",
    }),
    description: t(labels.CHANGE_DETECTOR_COLUMN_WIDTHS_CHANGED, {
      default: "Column widths changed",
    }),
    status: "pending",
    state: {},
    timestamp: now,
  };
}

export function detectColumnDecimalsChanges({
  columnDecimals,
  savedColumnDecimals,
  now,
  t,
}) {
  const allKeys = new Set([
    ...Object.keys(columnDecimals),
    ...Object.keys(savedColumnDecimals),
  ]);

  const hasChanges = [...allKeys].some(
    (key) => columnDecimals[key] !== savedColumnDecimals[key]
  );
  if (!hasChanges) return null;

  return {
    type: "columnDecimals",
    label: t(labels.CHANGE_DETECTOR_COLUMN_DECIMALS, {
      default: "Decimal Places",
    }),
    description: t(labels.CHANGE_DETECTOR_COLUMN_DECIMALS_CHANGED, {
      default: "Decimal places changed",
    }),
    status: "pending",
    state: {},
    timestamp: now,
  };
}

export function detectMetricsChanges({
  metricsItems,
  savedMetricsItems,
  now,
  t,
}) {
  const currentKeys = (metricsItems ?? []).map((m) => m.key).sort();
  const savedKeys = (savedMetricsItems ?? []).map((m) => m.key).sort();

  if (
    currentKeys.length === savedKeys.length &&
    currentKeys.every((k, i) => k === savedKeys[i])
  ) {
    return null;
  }

  const added = currentKeys.length - savedKeys.length;
  let description;
  if (added > 0) {
    const count = Math.abs(added);
    description = `${count} ${
      count === 1
        ? t(labels.CHANGE_DETECTOR_METRIC_ADDED, { default: "metric added" })
        : t(labels.CHANGE_DETECTOR_METRICS_ADDED, { default: "metrics added" })
    }`;
  } else if (added < 0) {
    const count = Math.abs(added);
    description = `${count} ${
      count === 1
        ? t(labels.CHANGE_DETECTOR_METRIC_REMOVED, {
            default: "metric removed",
          })
        : t(labels.CHANGE_DETECTOR_METRICS_REMOVED, {
            default: "metrics removed",
          })
    }`;
  } else {
    description = t(labels.CHANGE_DETECTOR_METRICS_CHANGED, {
      default: "Metrics changed",
    });
  }

  return {
    type: "metrics",
    label: t(labels.CHANGE_DETECTOR_METRICS, { default: "Metrics" }),
    description,
    status: "pending",
    state: {},
    timestamp: now,
  };
}

export function detectRenameChanges({
  columnNameOverrides,
  columnDisplayNameMap,
  now,
  t,
}) {
  const result = [];
  for (const [column, override] of Object.entries(columnNameOverrides)) {
    if (override === undefined) continue;
    const action = override === null ? "removed" : "renamed";
    const displayName = columnDisplayNameMap[column] || column;
    result.push({
      type: "rename",
      label: displayName,
      description:
        action === "removed"
          ? t(labels.CHANGE_DETECTOR_DISPLAY_NAME_RESET, {
              default: "Display name reset to default",
            })
          : `${t(labels.CHANGE_DETECTOR_RENAMED_TO, {
              default: "Renamed to",
            })} "${override}"`,
      status: "pending",
      state: {},
      timestamp: now,
      column,
    });
  }
  return result;
}

export function detectGroupByChanges({
  groupByColumns,
  savedGroupByColumns,
  columnAggregations,
  savedColumnAggregations,
  extraAggregateColumns = [],
  savedExtraAggregateColumns = [],
  rangeGroupings = {},
  savedRangeGroupings = {},
  includeDetails = false,
  savedIncludeDetails = false,
  includeTotal = false,
  savedIncludeTotal = false,
  includeSubTotal = false,
  savedIncludeSubTotal = false,
  allValuesEnumerations = new Set(),
  savedAllValuesEnumerations = new Set(),
  now,
  t,
}) {
  const groupByChanged =
    groupByColumns.size !== savedGroupByColumns.size ||
    [...groupByColumns].some((col) => !savedGroupByColumns.has(col));

  const hasGrouping = groupByColumns.size > 0 || savedGroupByColumns.size > 0;
  let aggregationChanged = false;
  if (hasGrouping) {
    const allCols = new Set([
      ...Object.keys(columnAggregations),
      ...Object.keys(savedColumnAggregations),
    ]);
    aggregationChanged = [...allCols].some(
      (col) => columnAggregations[col] !== savedColumnAggregations[col]
    );
  }

  const extraChanged =
    extraAggregateColumns.length !== savedExtraAggregateColumns.length ||
    extraAggregateColumns.some((e, i) => {
      const s = savedExtraAggregateColumns[i];
      return (
        !s ||
        e.column !== s.column ||
        e.func !== s.func ||
        e.sourceColumn !== s.sourceColumn
      );
    });

  const rangeGroupingsChanged = !isEqual(rangeGroupings, savedRangeGroupings);

  const detailsChanged =
    includeDetails !== savedIncludeDetails ||
    includeTotal !== savedIncludeTotal ||
    includeSubTotal !== savedIncludeSubTotal;

  const allValuesChanged =
    allValuesEnumerations.size !== savedAllValuesEnumerations.size ||
    [...allValuesEnumerations].some(
      (col) => !savedAllValuesEnumerations.has(col)
    );

  if (
    !groupByChanged &&
    !aggregationChanged &&
    !extraChanged &&
    !rangeGroupingsChanged &&
    !detailsChanged &&
    !allValuesChanged
  )
    return null;

  let description;
  if (groupByChanged && groupByColumns.size === 0) {
    description = t(labels.CHANGE_DETECTOR_GROUP_BY_ALL_UNGROUPED, {
      default: "All columns ungrouped",
    });
  } else if (groupByChanged) {
    const count = groupByColumns.size;
    description = t(labels.CHANGE_DETECTOR_GROUP_BY_COLUMNS_GROUPED, {
      default: `${count} column${count === 1 ? "" : "s"} grouped`,
      count,
    });
  } else if (extraChanged || rangeGroupingsChanged) {
    description = t(labels.CHANGE_DETECTOR_GROUP_BY_DERIVED_COLUMNS_CHANGED, {
      default: "Derived columns changed",
    });
  } else {
    description = t(labels.CHANGE_DETECTOR_GROUP_BY_AGGREGATION_CHANGED, {
      default: "Aggregation methods changed",
    });
  }

  return {
    type: "groupBy",
    label: t(labels.CHANGE_DETECTOR_GROUP_BY, { default: "Group By" }),
    description,
    status: "pending",
    state: {},
    timestamp: now,
  };
}

// Normalises summary-row state into the form that is actually written to the
// spec (null when switched off or empty), so change detection matches the JSON.
const normaliseSummary = (summaryType, summarySelections) => {
  const columns = Object.keys(summarySelections ?? {}).sort();
  if (summaryType === "OFF" || columns.length === 0) return null;
  return JSON.stringify({
    position: summaryType,
    columns: columns.map((c) => [c, summarySelections[c]]),
  });
};

export function detectSummaryChanges({
  summaryType = "BOTTOM",
  summarySelections = {},
  savedSummaryType = "BOTTOM",
  savedSummarySelections = {},
  now,
  t,
}) {
  const current = normaliseSummary(summaryType, summarySelections);
  const saved = normaliseSummary(savedSummaryType, savedSummarySelections);
  if (current === saved) return null;

  let description;
  if (current === null) {
    description = t(labels.CHANGE_DETECTOR_SUMMARY_REMOVED, {
      default: "Summary row removed",
    });
  } else if (saved === null) {
    description = t(labels.CHANGE_DETECTOR_SUMMARY_ADDED, {
      default: "Summary row added",
    });
  } else {
    description = t(labels.CHANGE_DETECTOR_SUMMARY_CHANGED, {
      default: "Summary row changed",
    });
  }

  return {
    type: "summary",
    label: t(labels.CHANGE_DETECTOR_SUMMARY, { default: "Summary Row" }),
    description,
    status: "pending",
    state: {},
    timestamp: now,
  };
}

export function detectSearchChanges({ searchQuery, savedSearchQuery, now, t }) {
  if (searchQuery === savedSearchQuery) return null;

  const description = searchQuery
    ? savedSearchQuery
      ? t(labels.CHANGE_DETECTOR_SEARCH_TEXT_CHANGED, {
          default: "Search text changed",
        })
      : t(labels.CHANGE_DETECTOR_SEARCH_FILTER_ADDED, {
          default: "Search filter added",
        })
    : t(labels.CHANGE_DETECTOR_SEARCH_FILTER_REMOVED, {
        default: "Search filter removed",
      });

  return {
    type: "search",
    label: t(labels.CHANGE_DETECTOR_SEARCH, { default: "Search" }),
    description,
    status: "pending",
    state: {},
    timestamp: now,
  };
}
